/**
 * RRULE parsing, building, and human-readable summaries for calendar events.
 *
 * Shared because both event editors must agree on the exact string they put on
 * the wire: web builds a rule and native reads it back (and now writes one), so
 * a divergence here shows up as an event that silently changes its recurrence
 * when edited from the other client.
 *
 * Rules here are stored and emitted **without** the `RRULE:` prefix — that is
 * what the events API expects. (Native's artifact `RecurrenceBlock` uses the
 * prefixed form for a different endpoint; do not copy strings between them.)
 */

import { format, getDate, getDay } from "date-fns";
import { ordinalSuffix } from "./ordinal.ts";

export const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
export const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

export type ParsedRrule = {
  freq: "daily" | "weekly" | "monthly" | "yearly";
  interval: number;
  days: string[];
  endType: "never" | "on" | "after";
  until?: Date;
  count?: number;
};

export function parseRrule(rrule: string): ParsedRrule {
  const parts = new Map(
    rrule.split(";").map((p) => {
      const [k, v] = p.split("=");
      return [k, v] as [string, string];
    }),
  );

  const freqMap: Record<string, ParsedRrule["freq"]> = {
    DAILY: "daily",
    WEEKLY: "weekly",
    MONTHLY: "monthly",
    YEARLY: "yearly",
  };

  const freq = freqMap[parts.get("FREQ") ?? ""] ?? "weekly";
  const interval = Number.parseInt(parts.get("INTERVAL") ?? "1", 10);
  const days = parts.get("BYDAY")?.split(",") ?? [];
  const untilStr = parts.get("UNTIL");
  const countStr = parts.get("COUNT");

  let endType: ParsedRrule["endType"] = "never";
  let until: Date | undefined;
  let count: number | undefined;

  if (untilStr) {
    endType = "on";
    const y = untilStr.slice(0, 4);
    const m = untilStr.slice(4, 6);
    const d = untilStr.slice(6, 8);
    until = new Date(Number(y), Number(m) - 1, Number(d));
  } else if (countStr) {
    endType = "after";
    count = Number.parseInt(countStr, 10);
  }

  return { freq, interval, days, endType, until, count };
}

export function buildRrule(parsed: ParsedRrule): string {
  const freqMap: Record<string, string> = {
    daily: "DAILY",
    weekly: "WEEKLY",
    monthly: "MONTHLY",
    yearly: "YEARLY",
  };
  const parts = [`FREQ=${freqMap[parsed.freq]}`];
  if (parsed.interval > 1) parts.push(`INTERVAL=${parsed.interval}`);
  if (parsed.days.length > 0 && (parsed.freq === "weekly" || parsed.freq === "monthly")) {
    parts.push(`BYDAY=${parsed.days.join(",")}`);
  }
  if (parsed.endType === "on" && parsed.until) {
    const d = format(parsed.until, "yyyyMMdd");
    parts.push(`UNTIL=${d}T235959Z`);
  } else if (parsed.endType === "after" && parsed.count) {
    parts.push(`COUNT=${parsed.count}`);
  }
  return parts.join(";");
}

const FULL_DAY_NAMES: Record<string, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};
const SHORT_DAY_NAMES: Record<string, string> = {
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
  SU: "Sun",
};

/**
 * `eventDate` supplies what the rule leaves implicit — a `FREQ=MONTHLY` with no
 * `BYMONTHDAY` repeats on the start date's day of month, so the summary can only
 * be written with the event in hand.
 */
export function rruleToText(rrule: string | undefined, eventDate: Date): string {
  if (!rrule) return "Does not repeat";
  const parsed = parseRrule(rrule);
  const { freq, interval, days } = parsed;

  let base = "";

  if (freq === "daily") {
    base = interval > 1 ? `Every ${interval} days` : "Every day";
  } else if (freq === "weekly") {
    const isWeekdays =
      days.length === 5 && ["MO", "TU", "WE", "TH", "FR"].every((d) => days.includes(d));
    if (isWeekdays) {
      base = interval > 1 ? `Every ${interval} weeks on weekdays` : "Every weekday";
    } else if (days.length > 0) {
      const dayStr = days.map((d) => SHORT_DAY_NAMES[d] ?? d).join(", ");
      base = interval > 1 ? `Every ${interval} weeks on ${dayStr}` : `Every week on ${dayStr}`;
    } else {
      const dayName = FULL_DAY_NAMES[DAY_CODES[getDay(eventDate)]];
      base = interval > 1 ? `Every ${interval} weeks on ${dayName}` : `Every week on ${dayName}`;
    }
  } else if (freq === "monthly") {
    const dom = getDate(eventDate);
    base =
      interval > 1
        ? `Every ${interval} months on the ${dom}${ordinalSuffix(dom)}`
        : `Every month on the ${dom}${ordinalSuffix(dom)}`;
  } else if (freq === "yearly") {
    const monthDay = format(eventDate, "MMM d");
    base = interval > 1 ? `Every ${interval} years on ${monthDay}` : `Every year on ${monthDay}`;
  }

  if (parsed.endType === "after" && parsed.count) {
    base += `, ${parsed.count} times`;
  } else if (parsed.endType === "on" && parsed.until) {
    base += `, until ${format(parsed.until, "MMM d, yyyy")}`;
  }

  return base;
}

export type RecurrencePreset = { label: string; rrule: string | undefined };

/**
 * The preset list both pickers offer. Labels embed the event's own day/date
 * ("Every week on Tuesday"), so the list is per-event rather than a constant.
 */
export function generatePresets(eventDate: Date): RecurrencePreset[] {
  const dayCode = DAY_CODES[getDay(eventDate)];
  const dayName = FULL_DAY_NAMES[dayCode];
  const dom = getDate(eventDate);
  const weekOfMonth = Math.ceil(dom / 7);
  // RRULE uses -1 for "last" weekday of month (5th week doesn't exist in RRULE spec)
  const isLastWeek = weekOfMonth >= 5 || dom > 28;
  const rruleWeekNum = isLastWeek ? -1 : weekOfMonth;
  const nthLabel = isLastWeek
    ? "last"
    : (["", "1st", "2nd", "3rd", "4th"][weekOfMonth] ?? `${weekOfMonth}th`);

  return [
    { label: "Does not repeat", rrule: undefined },
    { label: "Every day", rrule: "FREQ=DAILY" },
    { label: "Every weekday Mon–Fri", rrule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
    { label: `Every week on ${dayName}`, rrule: `FREQ=WEEKLY;BYDAY=${dayCode}` },
    { label: `Every 2 weeks on ${dayName}`, rrule: `FREQ=WEEKLY;INTERVAL=2;BYDAY=${dayCode}` },
    { label: `Every month on the ${dom}${ordinalSuffix(dom)}`, rrule: "FREQ=MONTHLY" },
    {
      label: `Every month on the ${nthLabel} ${dayName}`,
      rrule: `FREQ=MONTHLY;BYDAY=${rruleWeekNum}${dayCode}`,
    },
    { label: `Every year on ${format(eventDate, "MMM d")}`, rrule: "FREQ=YEARLY" },
  ];
}
